import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Clock, ArrowRight, Navigation, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { locations } from '../data/mock';
import Marquee from '../components/Marquee';
import StarBadge from '../components/StarBadge';

const Locations = () => {
  return (
    <div className="min-h-screen pt-28 bg-rush-cream">
      {/* Hero Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center bg-rush-mint/30 text-rush-navy rounded-full px-4 py-2 mb-6">
            <MapPin size={16} className="mr-2" />
            <span className="text-sm font-semibold">Our Locations</span>
          </span>
          <h1 className="text-5xl md:text-6xl font-display text-rush-navy mb-6">
            find rush<br />
            <span className="text-rush-blue">near you!</span>
          </h1>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Experience premium frozen yogurt at one of our locations across Nigeria, Ghana, and Lithuania. 🌍
          </p>
        </div>
      </section>

      {/* Marquee */}
      <Marquee text="VISIT US TODAY" bgColor="bg-rush-blue" />

      {/* Locations Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {locations.map((location, idx) => (
              <div
                key={location.id}
                className="bg-rush-cream rounded-3xl overflow-hidden hover:shadow-2xl transition-all duration-300 group hover:-translate-y-2"
              >
                {/* Location Header */}
                <div className="bg-rush-navy p-8 relative overflow-hidden">
                  <div className="absolute -top-4 -right-4">
                    <StarBadge 
                      text={idx === 0 ? 'HQ' : 'OPEN'} 
                      color={idx === 0 ? 'bg-rush-pink' : 'bg-rush-mint'}
                      textColor={idx === 0 ? 'text-white' : 'text-rush-navy'}
                      size="sm" 
                    />
                  </div>
                  <span className="text-7xl mb-4 block">{location.flag}</span>
                  <h2 className="text-3xl font-display text-white mb-1">{location.country}</h2>
                  <p className="text-white/70 text-lg">{location.city}</p>
                </div>

                {/* Location Details */}
                <div className="p-8">
                  <div className="space-y-4">
                    <div className="flex items-start space-x-4">
                      <div className="w-10 h-10 bg-rush-blue/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <MapPin className="text-rush-blue" size={20} />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide">Address</p>
                        <p className="text-gray-700">{location.address}</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="w-10 h-10 bg-rush-pink/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <Phone className="text-rush-pink" size={20} />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide">Phone</p>
                        <p className="text-gray-700">{location.phone}</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="w-10 h-10 bg-rush-mint/30 rounded-full flex items-center justify-center flex-shrink-0">
                        <Clock className="text-rush-navy" size={20} />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide">Hours</p>
                        <p className="text-gray-700">{location.hours}</p>
                      </div>
                    </div>
                  </div>

                  <Button className="w-full mt-8 bg-rush-blue hover:bg-rush-blue/90 text-white font-bold rounded-full group">
                    Get Directions
                    <Navigation className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-rush-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-display text-white mb-4">
              we're growing!
            </h2>
            <p className="text-white/70 text-lg max-w-2xl mx-auto">
              RUSH is expanding across Africa and Europe. Follow us @rushfroyo to stay updated on new location announcements. 📣
            </p>
          </div>

          <div className="grid grid-cols-3 gap-8 max-w-3xl mx-auto">
            <div className="text-center bg-white/10 rounded-3xl p-8">
              <p className="text-5xl font-display text-rush-mint mb-2">3</p>
              <p className="text-white/70">Countries</p>
            </div>
            <div className="text-center bg-white/10 rounded-3xl p-8">
              <p className="text-5xl font-display text-rush-pink mb-2">3+</p>
              <p className="text-white/70">Locations</p>
            </div>
            <div className="text-center bg-white/10 rounded-3xl p-8">
              <p className="text-5xl font-display text-rush-blue mb-2">2</p>
              <p className="text-white/70">Continents</p>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <Marquee text="COMING SOON TO YOUR CITY" bgColor="bg-rush-pink" />

      {/* Franchise CTA */}
      <section className="py-20 bg-rush-cream">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-display text-rush-navy mb-6">
            bring rush to your city!
          </h2>
          <p className="text-gray-600 text-lg mb-8 max-w-2xl mx-auto">
            Don't see a RUSH near you? Consider becoming a franchise partner and bring the frozen yogurt experience to your community. 🎉
          </p>
          <Link to="/franchise">
            <Button className="bg-rush-pink hover:bg-rush-pink/90 text-white font-bold px-10 py-6 text-lg rounded-full">
              Franchise Inquiry →
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Locations;
